/**
 * OpiekunkaPage — APISAI Growth Coach
 *
 * Spec: /app/memory/APISAI_OPIEKUNKA_SPEC.md
 * DNA:  /app/memory/APISAI_PRODUCT_DNA.md (HARD RULE #1, §6 Copy, §9 Breed)
 *
 * To NIE jest:
 *   ❌ chatbot z input na dole
 *   ❌ FAQ helper z pills
 *   ❌ Intercom clone
 *   ❌ support center
 *
 * To JEST:
 *   ✅ proactive growth dashboard
 *   ✅ premium AI coach z konkretnymi akcjami
 *   ✅ breed-aware + profile_type-aware
 *   ✅ 3 actionable suggestion cards (NIE chat)
 *
 * Free-form chat: dyskretnie na dole jako secondary,
 * główny ekran to PROAKTYWNE rekomendacje.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudioShell from '@/components/studio/StudioShell';
import { useGuidedProgress } from '@/hooks/useGuidedProgress';
import { toast } from 'sonner';
import {
  Sparkles, ArrowRight, Camera, MessageCircle, Megaphone, FileText, Globe2,
  Phone, Image as ImageIcon, BadgeCheck, TrendingUp, Loader2, Send,
} from 'lucide-react';

/* ════════════════ HELPERS ════════════════ */

/**
 * Generuje LOCAL fallback suggestions (przed integracją GPT-5.2).
 * Heurystyki on-device: patrzymy na progress signals + breed/profile_type.
 * Po wdrożeniu /api/coach/suggestions — to staje się fallback dla offline mode.
 */
function buildLocalSuggestions({ progress, primaryBreed, isBiznes, kennel }) {
  const out = [];
  const breedLabel = primaryBreed || (isBiznes ? 'Twojej firmy' : 'Twojej hodowli');

  // 1. Brak galerii / brak zdjęć
  const galleryCount = kennel?.gallery?.length || 0;
  if (galleryCount < 3) {
    out.push({
      id: 'add-gallery',
      icon: Camera,
      title: `Dodaj ${3 - galleryCount} zdjęcia galerii`,
      why: 'Klienci 5× częściej piszą do hodowli ze zdjęciami galerii.',
      effort: '2 minuty',
      cta_label: 'Dodaj zdjęcia',
      cta_route: '/panel/kreator-top',
      priority: 'high',
    });
  }

  // 2. Brak telefonu
  if (!kennel?.phone && !kennel?.whatsapp) {
    out.push({
      id: 'add-phone',
      icon: Phone,
      title: 'Dodaj numer telefonu lub WhatsApp',
      why: 'Bez kontaktu klient zniknie w 15 sekund. WhatsApp = 2× wyższa konwersja.',
      effort: '30 sekund',
      cta_label: 'Dodaj kontakt',
      cta_route: '/panel/kreator-top',
      priority: 'high',
    });
  }

  // 3. Brak postu promocyjnego
  if (!progress?.hasPosts) {
    out.push({
      id: 'first-post',
      icon: Megaphone,
      title: 'Opublikuj pierwszy post',
      why: `Pierwszy post promocyjny zwiększa odwiedziny strony średnio o 35%. ${breedLabel} szuka klient w Twojej okolicy.`,
      effort: '5 minut',
      cta_label: 'Stwórz post',
      cta_route: '/panel/promocja',
      priority: 'medium',
    });
  }

  // 4. Brak hero
  if (!progress?.hasHero) {
    out.unshift({
      id: 'create-hero',
      icon: Sparkles,
      title: 'Stwórz wygląd swojej strony',
      why: 'To pierwsza rzecz którą widzą klienci — kluczowa decyzja do podjęcia.',
      effort: '3 minuty',
      cta_label: 'Stwórz wygląd',
      cta_route: '/panel/kreator-top',
      priority: 'high',
    });
  }

  // 5. Brak profilu
  if (!progress?.hasProfile) {
    out.unshift({
      id: 'create-profile',
      icon: BadgeCheck,
      title: isBiznes ? 'Uzupełnij profil firmy' : 'Uzupełnij profil hodowli',
      why: 'Bez profilu nie pokażesz się w katalogu APISAI. To podstawa widoczności.',
      effort: '4 minuty',
      cta_label: 'Otwórz profil',
      cta_route: isBiznes ? '/wizard/firma-v2' : '/wizard/hodowla-v2',
      priority: 'high',
    });
  }

  // Fallback gdy WSZYSTKO zrobione
  if (out.length === 0) {
    out.push(
      {
        id: 'all-done-promo',
        icon: TrendingUp,
        title: 'Włącz promocję ofert',
        why: 'Twój profil jest gotowy. Czas dotrzeć do klientów lokalnych.',
        effort: '5 minut',
        cta_label: 'Zobacz promocję',
        cta_route: '/panel/promocja',
        priority: 'medium',
      },
      {
        id: 'all-done-share',
        icon: Globe2,
        title: 'Udostępnij swoją stronę',
        why: 'Pierwsi klienci zwykle przychodzą od znajomych. Wyślij link do najbliższych.',
        effort: '1 minuta',
        cta_label: 'Skopiuj link',
        cta_route: '/panel/moje',
        priority: 'medium',
      },
    );
  }

  // Max 3 sugestie (zgodnie ze spec'em)
  return out.slice(0, 3);
}

/* ════════════════ COMPONENTS ════════════════ */

function ProgressRing({ percent, accent }) {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={80} height={80} className="-rotate-90">
      <circle cx={40} cy={40} r={radius} stroke="rgba(4,32,47,0.08)" strokeWidth={6} fill="none" />
      <circle
        cx={40} cy={40} r={radius}
        stroke={accent}
        strokeWidth={6}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0.4, 0, 0.2, 1)' }}
      />
    </svg>
  );
}

function SignalChip({ icon: Icon, label, done, accent }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
      style={{
        background: done ? `${accent}15` : 'rgba(4,32,47,0.04)',
        color: done ? accent : 'rgba(4,32,47,0.45)',
      }}
    >
      <Icon className="w-3 h-3" strokeWidth={done ? 2.5 : 2} />
      <span>{label}</span>
      {done && <BadgeCheck className="w-3 h-3" strokeWidth={2.5} />}
    </div>
  );
}

/* ════════════════ MAIN PAGE ════════════════ */

export default function OpiekunkaPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const progress = useGuidedProgress();
  const accent = '#F97316'; // UI FREEZE: stały pomarańczowy akcent APISAI (bez breed-aware)

  const [kennel, setKennel] = useState(null);
  const [loading, setLoading] = useState(true);

  const isBiznes = user?.profile_type === 'biznes';
  const greetingName = user?.name?.split(' ')[0] || (isBiznes ? 'Witaj' : 'Hodowco');

  // Fetch realny kennel (do widoku signals chips + gallery count)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    const url = isBiznes ? '/api/business/my' : '/api/kennels';
    fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const items = Array.isArray(data) ? data : (data?.items || [data]);
        // Wybierz pierwszą z konkretną rasą (jak useGuidedProgress)
        const k = items.find((kk) => kk?.breeds?.[0] && kk.breeds[0].toLowerCase() !== 'mieszany') || items[0];
        setKennel(k || null);
      })
      .catch(() => setKennel(null))
      .finally(() => setLoading(false));
  }, [isBiznes]);

  // Compute completion percent (5 signals)
  const signals = [
    { key: 'hasProfile', label: 'Profil', icon: BadgeCheck,    done: !!progress?.hasProfile },
    { key: 'hasHero',    label: 'Wygląd', icon: Sparkles,      done: !!progress?.hasHero },
    { key: 'hasPhone',   label: 'Kontakt',icon: Phone,         done: !!(kennel?.phone || kennel?.whatsapp) },
    { key: 'hasGallery', label: 'Galeria',icon: ImageIcon,     done: (kennel?.gallery?.length || 0) >= 3 },
    { key: 'hasPosts',   label: 'Posty',  icon: Megaphone,     done: !!progress?.hasPosts },
  ];
  const doneCount = signals.filter((s) => s.done).length;
  const percent = Math.round((doneCount / signals.length) * 100);

  const suggestions = buildLocalSuggestions({
    progress,
    primaryBreed: progress?.primaryBreed,
    isBiznes,
    kennel,
  });

  // Greeting copy — proactive, breed/profile aware
  const insightLine = (() => {
    if (loading) return 'Sprawdzam Twój profil...';
    if (!progress?.hasProfile) return `Twoje konto czeka na uzupełnienie profilu — od tego zaczniemy.`;
    if (percent >= 80) return `Twoja ${isBiznes ? 'firma' : 'hodowla'} jest świetnie przygotowana — ${percent}% gotowości.`;
    if (percent >= 50) return `Twoja ${isBiznes ? 'firma' : 'hodowla'} jest w połowie drogi — ${percent}% gotowości. Brakuje 2-3 rzeczy do pełni.`;
    return `Sprawdziłam Twój profil. Mam ${suggestions.length} konkretne podpowiedzi na dzisiaj.`;
  })();

  const accentStyle = {
    '--breed-accent': accent,
    '--breed-accent-soft': `${accent}1A`,
    '--breed-accent-ring': `${accent}40`,
  };

  return (
    <StudioShell
      mode="workspace"
      contextLabel="Twoja Pomoc"
      activeKey="pomoc"
      sidebarMuted={false}
    >
      <div style={accentStyle} className="min-h-full" data-testid="opiekunka-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14">

          {/* ═══════════════ HERO ═══════════════ */}
          <section className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-12 items-center mb-10" data-testid="opiekunka-hero">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                  style={{ background: accent }}
                >
                  <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: accent }}>
                  Twoja Pomoc APISAI
                </p>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#04202F] leading-[1.05]">
                Cześć, {greetingName}!
                <span className="block mt-2 text-[#04202F]/55 text-2xl sm:text-3xl lg:text-4xl font-semibold">
                  {insightLine}
                </span>
              </h1>
              <div className="flex flex-wrap gap-1.5 pt-2" data-testid="opiekunka-signals">
                {signals.map((s) => (
                  <SignalChip key={s.key} icon={s.icon} label={s.label} done={s.done} accent={accent} />
                ))}
              </div>
            </div>
            {/* Progress ring + summary */}
            <div className="flex items-center gap-5 justify-self-start lg:justify-self-end">
              <div className="relative flex-shrink-0">
                <ProgressRing percent={percent} accent={accent} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-[#04202F]">{percent}%</span>
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[#04202F]/55 text-[11px] uppercase tracking-wider font-bold">Gotowość</p>
                <p className="text-[#04202F] text-base font-semibold mt-0.5 leading-tight">
                  {doneCount} z {signals.length} kroków
                </p>
                <p className="text-[#04202F]/45 text-[11px] mt-1">
                  {suggestions.length} {suggestions.length === 1 ? 'sugestia' : 'sugestie'} na dziś
                </p>
              </div>
            </div>
          </section>

          {/* ═══════════════ TODAY'S 3 ACTIONS ═══════════════ */}
          <section className="space-y-5" data-testid="opiekunka-suggestions">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#04202F] tracking-tight">
                  Co dziś najbardziej pomoże Twojej {isBiznes ? 'firmie' : 'hodowli'}?
                </h2>
                <p className="text-[#04202F]/55 text-sm sm:text-base mt-1">
                  Trzy rzeczy które wybrałam dla Ciebie — każda zajmie kilka minut, efekt zobaczysz od razu.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="lg:col-span-3 flex justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} />
                </div>
              ) : (
                suggestions.map((s, idx) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      data-testid={`opiekunka-suggestion-${idx + 1}`}
                      onClick={() => {
                        if (s.cta_route) navigate(s.cta_route);
                      }}
                      className="group relative text-left bg-white rounded-3xl p-6 border transition-all hover:-translate-y-1"
                      style={{
                        borderColor: 'rgba(4,32,47,0.08)',
                        boxShadow: '0 12px 32px -16px rgba(4,32,47,0.08)',
                      }}
                    >
                      {/* Priority numer w breed-accent circle */}
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md"
                          style={{ background: accent }}
                        >
                          {idx + 1}
                        </div>
                        <div
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ background: `${accent}14`, color: accent }}
                        >
                          <span>⏱</span>
                          {s.effort}
                        </div>
                      </div>
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                        style={{ background: `${accent}14` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={1.8} />
                      </div>
                      <h3 className="text-[#04202F] text-lg font-bold leading-tight mb-2">
                        {s.title}
                      </h3>
                      <p className="text-[#04202F]/60 text-sm leading-relaxed mb-4">
                        {s.why}
                      </p>
                      <div
                        className="inline-flex items-center gap-1.5 text-sm font-semibold transition-transform group-hover:translate-x-1"
                        style={{ color: accent }}
                      >
                        {s.cta_label}
                        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <p className="text-center text-[#04202F]/40 text-xs italic pt-3">
              Chcesz, żebym pokazała kolejne 3 kroki kiedy te będą gotowe?
            </p>
          </section>

          {/* ═══════════════ FREE-FORM (secondary, dyskretnie) ═══════════════ */}
          <section className="mt-16 pt-10 border-t border-[#04202F]/06" data-testid="opiekunka-freeform">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
              <div className="flex items-center gap-2.5">
                <MessageCircle className="w-4 h-4" style={{ color: accent }} />
                <h3 className="text-[#04202F] text-base font-bold">Masz inne pytanie?</h3>
              </div>
              <p className="text-[#04202F]/50 text-sm">
                Zapytaj o cokolwiek — od marketingu po szczeniaki.
              </p>
            </div>
            <div
              className="relative bg-white rounded-2xl border p-2 flex items-center gap-2"
              style={{ borderColor: 'rgba(4,32,47,0.08)' }}
            >
              <input
                type="text"
                placeholder={isBiznes ? 'Np. Jak zwiększyć liczbę zapytań od klientów?' : 'Np. Kiedy najlepiej zacząć promocję miotu?'}
                className="flex-1 bg-transparent px-3 py-3 text-sm text-[#04202F] placeholder-[#04202F]/35 focus:outline-none"
                data-testid="opiekunka-chat-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    toast('Wkrótce: pełen chat z kontekstem Twojej hodowli — uruchomimy w następnej iteracji 🚀', { duration: 4000 });
                  }
                }}
              />
              <button
                onClick={() => toast('Wkrótce: pełen chat z kontekstem Twojej hodowli — uruchomimy w następnej iteracji 🚀', { duration: 4000 })}
                className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white transition-transform hover:scale-105"
                style={{ background: accent }}
                data-testid="opiekunka-chat-send"
              >
                <Send className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            <p className="text-[#04202F]/35 text-[11px] mt-2 italic">
              Beta — pełen chat z kontekstem Twojej hodowli uruchomimy wkrótce.
            </p>
          </section>

        </div>
      </div>
    </StudioShell>
  );
}
